<template>
  <div class="row">
    <div class="col-12">
      <card card-body-classes="table-full-width" no-footer-line>
        <h4 slot="header" class="card-title">{{ $t("message.customers") }}</h4>
        <div class="card-body">
          <b-form class="d-flex mb-2" inline @submit.prevent>
            <b-form-select
              v-model="pagination.perPage"
              aria-controls="customersTable"
              :options="pagination.perPageOptions"
            >
            </b-form-select>
            <fg-input
              id="customersTable__searchQuery"
              class="ml-2"
              v-model="searchQuery"
              placeholder="Search..."
              aria-label="Search"
              aria-controls="customersTable"
            ></fg-input>
            <router-link
              v-if="$can('create', 'CUSTOMERS')"
              class="ml-auto mt-0 btn btn-primary"
              to="/customers/add"
            >
              {{ $t("message.add") }}
            </router-link>
          </b-form>
          <b-table
            id="customersTable"
            :items="customers"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :filter="searchQuery"
            :per-page="pagination.perPage"
            :current-page="pagination.currentPage"
            :busy="isLoading"
            :show-empty="!isLoading && !customers.length"
            stacked="md"
            striped
            @filtered="
              filteredItems => (searchItemsLength = filteredItems.length)
            "
          >
            <template v-slot:table-busy>
              <div class="text-center text-primary my-2">
                <b-spinner class="align-middle"></b-spinner>
              </div>
            </template>
            <template v-slot:empty>
              <div v-if="error" class="text-center text-danger my-2">
                <p>Something went wrong, please try again later.</p>
                <p>{{ error }}</p>
              </div>
              <div v-else class="text-center text-primary my-2">
                <p>You haven't added any Customers.</p>
              </div>
            </template>
            <template v-slot:cell(companyName)="data">
              <router-link tag="a" :to="`/customers/view/${data.item._id}`">{{
                data.item.companyName
              }}</router-link>
            </template>
            <template v-slot:cell(actions)="data">
              <drop-down
                class="row-actions"
                tag="button"
                position="right"
                icon="fa fa-ellipsis-v"
                hideArrow
                v-if="$can('update', 'CUSTOMERS')"
              >
                <router-link
                  tag="a"
                  class="dropdown-item"
                  :to="`/customers/edit/${data.item._id}`"
                  :disabled="!$can('update', 'CUSTOMERS')"
                  >{{ $t("message.edit") }}</router-link
                >
                <router-link
                  v-if="$can('create', 'INVOICES')"
                  tag="a"
                  class="dropdown-item"
                  :to="`/invoices/add?customer_id=${data.item._id}`"
                  >{{ $t("message.newInvoice") }}</router-link
                >
                <router-link
                  v-if="$can('create', 'RECURRING_INVOICES')"
                  tag="a"
                  class="dropdown-item"
                  :to="`/recurring-invoices/add?customer_id=${data.item._id}`"
                  >{{ $t("message.newRecurringInvoice") }}</router-link
                >
                <button
                  v-if="$can('delete', 'CUSTOMERS')"
                  class="dropdown-item text-danger"
                  @click="handleDeleteCustomer(data.item)"
                >
                  {{ $t("message.delete") }} {{ $t("message.customer") }}
                </button>
              </drop-down>
            </template>
          </b-table>
          <div class="d-flex justify-content-between">
            <div>
              <span class="card-category">
                Showing {{ from + 1 }} to {{ to }} of {{ total }} entries
              </span>
            </div>
            <n-pagination
              class="pagination-no-border"
              v-model="pagination.currentPage"
              :per-page="pagination.perPage"
              :total="total"
            />
          </div>
        </div>
      </card>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { Pagination as NPagination } from "src/components";
import deleteCustomerMixin from "./deleteCustomerMixin";

export default {
  mixins: [deleteCustomerMixin],
  components: {
    NPagination
  },
  data() {
    return {
      loader: null,
      fields: [
        {
          key: "companyName",
          label: this.$t("message.name"),
          sortable: true
        },
        {
          key: "emailAddress",
          label: this.$t("message.companyEmailAddress"),
          sortable: true
        },
        {
          key: "vatNumber",
          label: this.$t("message.companyVatNumber"),
          sortable: true
        },
        {
          key: "contactName",
          label: this.$t("message.contactName"),
          sortable: true
        },
        {
          key: "contactNumber",
          label: this.$t("message.contactNumber"),
          sortable: true
        },
        {
          key: "actions",
          label: this.$t("message.actions"),
          sortable: false
        }
      ],
      searchQuery: null,
      searchItemsLength: 0,
      sortBy: "name",
      sortDesc: false,
      pagination: {
        perPage: 5,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50]
      },
      isLoading: false,
      error: null
    };
  },
  methods: {
    ...mapActions(["fetchCustomers"]),
    showLoadingIndicator() {
      this.isLoading = true;
    },
    hideLoadingIndicator() {
      this.isLoading = false;
    },
    showError(error) {
      this.error = error;
    }
  },
  computed: {
    ...mapGetters(["customers"]),
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.searchQuery ? this.searchItemsLength : this.customers.length;
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    }
  },
  async created() {
    try {
      this.showLoadingIndicator();
      await this.fetchCustomers();
    } catch (e) {
      this.showError(e.message);
    } finally {
      this.hideLoadingIndicator();
    }
  }
};
</script>
